import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import MainLayout from "@/components/layout/MainLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Loader2, CalendarCheck } from "lucide-react";
import { toast } from "sonner";
import { useSuccessionStageState } from "@/lib/succession/stages";

type DrillRow = {
  id: string;
  drill_date: string;
  path_walked: string | null;
  minute_text: string | null;
  next_due: string | null;
};

type CredCategory = { category: string; count: number };

function addOneYear(dateStr: string): string {
  const d = new Date(dateStr);
  d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().slice(0, 10);
}

function formatLongDate(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
}

export default function SuccessionRhythm() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const s = useSuccessionStageState(user?.id);

  const [drills, setDrills] = useState<DrillRow[]>([]);
  const [categories, setCategories] = useState<CredCategory[]>([]);
  const [drillDate, setDrillDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [confirm1, setConfirm1] = useState(false);
  const [confirm2, setConfirm2] = useState(false);
  const [confirm3, setConfirm3] = useState(false);
  const [confirm4, setConfirm4] = useState(false);
  const [pathCategory, setPathCategory] = useState<string>("");
  const [minuteText, setMinuteText] = useState("");
  const [minuteEdited, setMinuteEdited] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!s.profileId || !s.unaId) return;
    (async () => {
      const [{ data: drillRows }, { data: creds }] = await Promise.all([
        supabase.from("succession_drills")
          .select("id, drill_date, path_walked, minute_text, next_due")
          .eq("profile_id", s.profileId).order("drill_date", { ascending: false }),
        supabase.from("credential_map_items")
          .select("category").eq("association_id", s.unaId),
      ]);
      setDrills((drillRows as any) || []);
      const bag = new Map<string, number>();
      for (const r of (creds || [])) {
        const c = (r as any).category as string;
        bag.set(c, (bag.get(c) || 0) + 1);
      }
      const cats = Array.from(bag.entries()).map(([category, count]) => ({ category, count }));
      setCategories(cats);
      if (cats.length && !pathCategory) setPathCategory(cats[0].category);
    })();
  }, [s.profileId, s.unaId]);

  const draftMinute = useMemo(() => {
    if (!drillDate) return "";
    const pretty = formatLongDate(drillDate);
    const cat = pathCategory || "key map";
    return `On ${pretty}, the Board read the named successors and found them still right, checked the key map, and walked the ${cat} way in with success. This drill is entered into the record.`;
  }, [drillDate, pathCategory]);

  useEffect(() => {
    if (!minuteEdited) setMinuteText(draftMinute);
  }, [draftMinute, minuteEdited]);

  const canSave = confirm1 && confirm2 && confirm3 && confirm4 && !!drillDate && !!minuteText.trim();

  async function saveDrill() {
    if (!s.profileId || !s.unaId || !canSave) return;
    setSaving(true);
    const next_due = addOneYear(drillDate);
    const { data, error } = await supabase.from("succession_drills").insert({
      association_id: s.unaId,
      profile_id: s.profileId,
      drill_date: drillDate,
      path_walked: pathCategory || null,
      minute_text: minuteText.trim(),
      next_due,
      created_by: user?.id,
    }).select("id, drill_date, path_walked, minute_text, next_due").single();
    setSaving(false);
    if (error) { toast.error("Could not save the drill."); return; }
    toast.success("Drill entered into the record. See you next year.");
    setDrills((prev) => [data as any, ...prev]);
    setConfirm1(false); setConfirm2(false); setConfirm3(false); setConfirm4(false);
    setMinuteEdited(false);

    // Seed / roll the annual drill task into Governance Tasks. Follows the
    // AttestationRenewal pattern: update any existing open task, otherwise insert.
    try {
      const TASK_TITLE = "Annual succession drill";
      const TASK_DESCRIPTION =
        "Twenty minutes, four checks and one walk. Open the Rhythm stage in Succession to run it.";
      const { data: existingOpen } = await supabase
        .from("custom_tasks")
        .select("id")
        .eq("una_id", s.unaId)
        .eq("task_title", TASK_TITLE)
        .neq("status", "completed")
        .limit(1)
        .maybeSingle();
      if (existingOpen?.id) {
        await supabase
          .from("custom_tasks")
          .update({ due_date: next_due, task_description: TASK_DESCRIPTION })
          .eq("id", existingOpen.id);
      } else if (user?.id) {
        await supabase.from("custom_tasks").insert({
          una_id: s.unaId,
          user_id: user.id,
          task_title: TASK_TITLE,
          task_description: TASK_DESCRIPTION,
          category_type: "governance",
          status: "not_started",
          importance: "high",
          urgency: "medium",
          due_date: next_due,
          recurring_config: {
            enabled: true,
            pattern: "yearly",
            interval: 1,
            frequency_label: "Annually",
          },
        });
      }
    } catch (e) {
      console.error("Could not seed the annual drill task:", e);
    }
  }


  if (s.loading) {
    return (
      <MainLayout>
        <div className="max-w-5xl mx-auto py-12 px-4">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </MainLayout>
    );
  }

  if (!s.unaId) {
    return (
      <MainLayout>
        <div className="max-w-3xl mx-auto py-12 px-4">
          <Card className="p-6"><p>You must be an Association administrator to use this page.</p></Card>
        </div>
      </MainLayout>
    );
  }

  if (s.isPaused) {
    return (
      <MainLayout>
        <div className="max-w-3xl mx-auto py-12 px-4">
          <Card className="p-6"><p className="text-amber-900">Your succession record is paused for review. Our team will reach out.</p></Card>
        </div>
      </MainLayout>
    );
  }

  if (!s.stage6Unlocked) {
    return (
      <MainLayout>
        <div className="max-w-3xl mx-auto py-12 px-4">
          <Card className="p-6 space-y-3">
            <h1 className="text-2xl font-serif">Rhythm</h1>
            <p className="text-muted-foreground">Finish Execution first.</p>
            <Button onClick={() => navigate("/succession/execution")}>Go to Execution</Button>
          </Card>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="max-w-4xl mx-auto py-8 px-4 space-y-8">
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate("/succession/overview")}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Succession
          </Button>
          {s.stage6Done && <Badge className="bg-emerald-600 hover:bg-emerald-600">Rhythm established</Badge>}
        </div>

        <div>
          <h1 className="text-3xl font-serif">Rhythm</h1>
          <p className="text-muted-foreground mt-2">The yearly drill that proves it works.</p>
        </div>

        <Card className="p-6 space-y-2">
          <p className="text-sm leading-relaxed">
            A plan you never test is a plan you only hope in. Once a year, twenty minutes, four checks and one walk. That is what turns your plan from paper into something you can count on.
          </p>
        </Card>

        <Card className="p-6 space-y-5">
          <div className="flex items-center gap-2">
            <CalendarCheck className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-serif">This year's drill</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label htmlFor="drill-date">Drill date</Label>
              <input id="drill-date" type="date"
                value={drillDate}
                onChange={(e) => { setDrillDate(e.target.value); setMinuteEdited(false); }}
                className="w-full border rounded-md h-10 px-3 bg-background" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="drill-cat">Access path to walk</Label>
              <Select value={pathCategory} onValueChange={(v) => { setPathCategory(v); setMinuteEdited(false); }}>
                <SelectTrigger id="drill-cat">
                  <SelectValue placeholder={categories.length ? "Pick a category" : "No credential map entries yet"} />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c.category} value={c.category}>{c.category} ({c.count})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-3">
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox checked={confirm1} onCheckedChange={(v) => setConfirm1(!!v)} className="mt-1" />
              <div>
                <p className="text-sm">Named successors read and still right</p>
                <p className="text-xs text-muted-foreground">If names or order changed, adopt a fresh Standing Successor Resolution in your documents first.</p>
              </div>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox checked={confirm2} onCheckedChange={(v) => setConfirm2(!!v)} className="mt-1" />
              <p className="text-sm">Incapacity Protocol read again</p>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox checked={confirm3} onCheckedChange={(v) => setConfirm3(!!v)} className="mt-1" />
              <p className="text-sm">Key map checked against what is really there</p>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox checked={confirm4} onCheckedChange={(v) => setConfirm4(!!v)} className="mt-1" />
              <p className="text-sm">We walked this way in and the successor could really reach it</p>
            </label>
          </div>

          <div className="space-y-1">
            <Label htmlFor="drill-minute">Minute</Label>
            <Textarea id="drill-minute" rows={4} value={minuteText}
              onChange={(e) => { setMinuteText(e.target.value); setMinuteEdited(true); }} />
          </div>

          <Button onClick={saveDrill} disabled={!canSave || saving}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Enter this drill into the record
          </Button>
        </Card>

        {drills.length > 0 && (
          <Card className="p-6 space-y-3">
            <h3 className="text-lg font-serif">Drill history</h3>
            <div className="space-y-3">
              {drills.map((d) => (
                <div key={d.id} className="border rounded-md p-3">
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-sm">{formatLongDate(d.drill_date)}</p>
                    {d.next_due && <span className="text-xs text-muted-foreground">Next due {formatLongDate(d.next_due)}</span>}
                  </div>
                  {d.path_walked && <p className="text-xs text-muted-foreground mt-1">Walked: {d.path_walked}</p>}
                  {d.minute_text && <p className="text-sm mt-2 whitespace-pre-wrap">{d.minute_text}</p>}
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </MainLayout>
  );
}
